import {
    Component,
    EventEmitter,
    ViewEncapsulation,
    Output,
    OnInit,
} from '@angular/core';
import { Location, Countries, Provinces } from '../../model/country';
import {
    FormBuilder,
    Validators,
    FormGroup,
    FormControl,
    AbstractControl,
} from '@angular/forms';
import { Country } from '@viewbid/ts-angular-client';
import { phoneNumberValidator } from '../../validators/phone-number-validator';
import {
    postalCodeFormatHint,
    postalCodeValidator,
} from '../../validators/postal-code-validator';
import {
    STREET_LINE_SIGN_HINT,
    requiredStreetLineValidator,
    streetLineValidator,
} from '../../validators/street-line-validator';

@Component({
    selector: 'address-section',
    templateUrl: './create-address-section.component.html',
    styleUrls: ['./create-address-section.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class CreateAddressSectionComponent implements OnInit {
    @Output() public sectionChanged = new EventEmitter<void>();
    countries = Countries;
    allCountries = Country;
    selectedCountry = Country.Ca;
    provinceLabel = 'Province';
    postalCodeLabel = 'Postal Code';

    addressFormGroup: FormGroup = this._formBuilder.group({
        country: new FormControl('', [
            Validators.required,
            Validators.maxLength(45),
        ]),
        phoneNumber: new FormControl('', [
            Validators.required,
            phoneNumberValidator(),
        ]),
        address1: new FormControl('', [
            requiredStreetLineValidator(),
            Validators.maxLength(45),
            streetLineValidator(),
        ]),
        address2: new FormControl('', [
            Validators.maxLength(45),
            streetLineValidator(),
        ]),
        city: new FormControl('', [
            Validators.required,
            Validators.maxLength(45),
        ]),
        province: new FormControl('', [
            Validators.required,
            Validators.maxLength(45),
        ]),
        postalCode: new FormControl('', [
            Validators.required,
            Validators.maxLength(10),
            postalCodeValidator(),
        ]),
        buzzer: new FormControl('', [Validators.maxLength(10)]),
    });

    constructor(private _formBuilder: FormBuilder) {}
    ngOnInit(): void {
        this.addressFormGroup.valueChanges.subscribe(() => {
            this.sectionChanged.emit();
        });
    }

    onCountrySelected(event: any): void {
        this.selectedCountry = event.value;
        if (event.value == Country.Us) {
            this.provinceLabel = 'State';
            this.postalCodeLabel = 'ZIP';
            this.addressFormGroup.controls['postalCode'].setValue('');
            this.addressFormGroup.controls['province'].setValue('');
        } else {
            this.provinceLabel = 'Province';
            this.postalCodeLabel = 'Postal Code';
            this.addressFormGroup.controls['postalCode'].setValue('');
            this.addressFormGroup.controls['province'].setValue('');
        }
    }

    get sortedProvincesCanada(): Location[] {
        return Provinces.filter(
            (province: Location) => province.country === this.selectedCountry,
        ).sort((input: Location, filter: Location) =>
            input.value.localeCompare(filter.value),
        );
    }

    isFormValid(): boolean {
        return this.addressFormGroup.valid;
    }

    markAllAsTouched(): void {
        this.addressFormGroup.markAllAsTouched();
    }

    getHelperText(field: string): string | null {
        const control: AbstractControl<any, any> | null =
            this.addressFormGroup.get(field);

        // If no control found, return empty
        if (!control) {
            return null;
        }

        const requiredValidation: boolean =
            control.hasError('required') && control?.touched;
        const maxLengthValidation: boolean =
            control.hasError('maxlength') && control?.touched;
        const postalCodeValidation: boolean =
            control.hasError('postalCode') && control?.touched;
        const leadingSignValidation: boolean =
            control.hasError('leadingSign') && control?.touched;
        switch (field) {
            case 'address1':
                if (requiredValidation) {
                    return 'Address is required';
                }
                if (maxLengthValidation) {
                    return `Address can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (leadingSignValidation) {
                    return STREET_LINE_SIGN_HINT;
                }
                break;
            case 'address2':
                if (maxLengthValidation) {
                    return `Address can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (leadingSignValidation) {
                    return STREET_LINE_SIGN_HINT;
                }
                break;
            case 'city':
                if (requiredValidation) {
                    return 'City is required';
                }
                if (maxLengthValidation) {
                    return `City can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'province':
                if (requiredValidation) {
                    return 'Province is required';
                }
                if (maxLengthValidation) {
                    return `Province can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'postalCode':
                if (requiredValidation) {
                    return 'Postal Code is required';
                }
                if (maxLengthValidation) {
                    return `Postal Code can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (postalCodeValidation) {
                    return postalCodeFormatHint(this.selectedCountry);
                }
                break;
            case 'country':
                if (requiredValidation) {
                    return 'Country is required';
                }
                if (maxLengthValidation) {
                    return `Country can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'buzzer':
                if (maxLengthValidation) {
                    return `Buzzer can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'phoneNumber':
                if (control.errors) {
                    if (control.errors['phoneNumber'] === 'TOO_SHORT') {
                        return 'Too Short, please enter a valid number';
                    }
                    if (control.errors['phoneNumber'] === 'NOT_A_NUMBER') {
                        return 'Not a number, please enter a valid number';
                    }
                    return control.errors['phoneNumber'];
                }
                break;
            default:
                return null;
        }

        return null;
    }
}
